import React, { useState } from 'react';
import { Alert } from '@mui/material';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import useTranslate from '../../../../../customHooks/useTranslate';
import {
    useContractDetail,
    useContractDetailData,
} from '../../../../contextsProperty/ContextContractDetail';
import SwitchInput from '../../../../../muiComponents/switchInput/SwitchInput';
import IconButton from '../../../../../muiComponents/IconButton';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import {
    doubleMD_ColumnGrid,
    full_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../../utils/commonVariables/grid';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import DateInput from '../../../../../muiComponents/DateInput';
import Button from '../../../../../muiComponents/Button';
import { SEASON_VALIDATION_INITIAL_STATE } from '../../../../../utils/contractUtils/contractUtils';
import { validateForm } from '../../../../../utils/validateForms';
import { convertDateToDayjs } from '../../../../../utils/formatDates';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import DeletePopUp from '../DeletePopUp';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import LoadingPanel from './LoadingPanel';
import PanelTitle from '../../../yourProperties/Title';
import useUserProfile from '../../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../../ReadOnlyUserAlert';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface SeasonPanelProps {
    open: boolean;
    onClose: () => void;
}

const SeasonPanel = (props: SeasonPanelProps) => {
    const { open, onClose } = props;
    const translate = useTranslate();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { readOnlyUser } = useUserProfile();

    const [deletePopUpVisible, setDeletePopUpVisible] = useState(false);

    const {
        isLoadingPanel,
        handleLoadingPanel,
        isBlockedContract,
        seasonEdition,
        handleChangeSeasonEdition,
        handleSaveSeason,
    } = useContractDetail();
    const { activeFrom, activeTo, seasons } = useContractDetailData();

    const isNewSeason = !seasonEdition.id || parseInt(seasonEdition.id) <= 0;

    const [validatedForm, setValidatedForm] = useState(false);
    const [formErrors, setFormErrors] = useState(
        SEASON_VALIDATION_INITIAL_STATE
    );

    const fieldsToValidate = {
        name: {
            value: seasonEdition.name,
            validators: ['requiredString'],
        },
        start: {
            value: seasonEdition.start,
            validators: ['requiredDate'],
        },
        end: {
            value: seasonEdition.end,
            validators: ['requiredDate'],
        },
    };

    const validateInputsForm = () => {
        setFormErrors(SEASON_VALIDATION_INITIAL_STATE);
        const errors = validateForm(fieldsToValidate);
        if (errors.length) {
            for (const error of errors) {
                setFormErrors(prev => ({
                    ...prev,
                    [error.inputKey]: error.errorMessage,
                }));
            }
        }
        return errors.length === 0;
    };

    return (
        <>
            {deletePopUpVisible && !readOnlyUser && (
                <DeletePopUp
                    open={!!deletePopUpVisible}
                    onSave={async () => {
                        try {
                            handleLoadingPanel(true);
                            await handleSaveSeason({
                                ...seasonEdition,
                                Deleted: true,
                            });
                            setSuccessMessage(
                                FeedbackMessages.COMMON_SUCCESS_DELETE
                            );
                        } catch (error) {
                            setErrorMessage(FeedbackMessages.COMMON_ERROR);
                        } finally {
                            handleLoadingPanel(false);
                            setDeletePopUpVisible(false);
                            onClose();
                        }
                    }}
                    onCancel={() => {
                        setDeletePopUpVisible(false);
                    }}
                ></DeletePopUp>
            )}
            <LateralPanel open={open}>
                {isLoadingPanel ? (
                    <LoadingPanel />
                ) : (
                    <form className='flex-column height100perc flex-between'>
                        <div className='flex-column  gap-20'>
                            <div className='flex-between flex-align-center contract-panel__title'>
                                <PanelTitle
                                    text={
                                        isNewSeason
                                            ? 'new_season'
                                            : seasonEdition.name
                                    }
                                    className='text-secondary-color'
                                    translated={isNewSeason}
                                />
                                <div className='flex-between gap-10'>
                                    <SwitchInput
                                        labelText={''}
                                        checked={seasonEdition.enabled}
                                        disabled={
                                            isBlockedContract || readOnlyUser
                                        }
                                        onChangeMethod={() => {
                                            handleChangeSeasonEdition({
                                                enabled: !seasonEdition.enabled,
                                            });
                                        }}
                                    />
                                    <IconButton
                                        iconName='Close'
                                        onClick={onClose}
                                    />
                                </div>
                            </div>

                            {readOnlyUser ? (
                                <ReadOnlyUserAlert text='read_only_contract' />
                            ) : (
                                isBlockedContract && (
                                    <Alert severity='info'>
                                        {translate(
                                            'bloqued_information_contract'
                                        )}
                                    </Alert>
                                )
                            )}

                            <div className='flex-column  gap-10 '>
                                <Grid
                                    style={{ width: 'auto' }}
                                    columnSpacing={gridSpacing30}
                                    rowSpacing={gridSpacing20}
                                    className='border-color-block padding-block-20'
                                >
                                    <GridChild gridProps={full_ColumnGrid}>
                                        <TextInput
                                            required
                                            id='name'
                                            labelText='name'
                                            value={seasonEdition.name}
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            handleChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleChangeSeasonEdition({
                                                    name:
                                                        e?.target?.value || '',
                                                })
                                            }
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                            error={!!formErrors?.name}
                                            errorMessage={formErrors?.name}
                                        />
                                    </GridChild>
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <DateInput
                                            required
                                            labelText='from'
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            value={seasonEdition.start}
                                            minDate={activeFrom}
                                            maxDate={activeTo}
                                            error={!!formErrors?.start}
                                            errorMessage={formErrors?.start}
                                            onChange={(date: Date | null) => {
                                                handleChangeSeasonEdition({
                                                    start: date,
                                                });
                                            }}
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                            shouldDisableDate={(day: Date) => {
                                                const date =
                                                    convertDateToDayjs(day);
                                                if (
                                                    date &&
                                                    (date.isBefore(
                                                        convertDateToDayjs(
                                                            activeFrom
                                                        ),
                                                        'day'
                                                    ) ||
                                                        date.isAfter(
                                                            convertDateToDayjs(
                                                                activeTo
                                                            ),
                                                            'day'
                                                        ))
                                                ) {
                                                    return true;
                                                }
                                                if (
                                                    seasonEdition.end &&
                                                    date &&
                                                    date.isAfter(
                                                        convertDateToDayjs(
                                                            seasonEdition.end
                                                        )
                                                    )
                                                ) {
                                                    return false;
                                                }
                                                return seasons.some(
                                                    range =>
                                                        date &&
                                                        date.isSameOrAfter(
                                                            convertDateToDayjs(
                                                                range.start
                                                            ),
                                                            'day'
                                                        ) &&
                                                        date.isSameOrBefore(
                                                            convertDateToDayjs(
                                                                range.end
                                                            ),
                                                            'day'
                                                        )
                                                );
                                            }}
                                        />
                                    </GridChild>
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <DateInput
                                            required
                                            labelText='to'
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            value={seasonEdition.end}
                                            minDate={
                                                seasonEdition.start ||
                                                activeFrom
                                            }
                                            maxDate={activeTo}
                                            error={!!formErrors?.end}
                                            errorMessage={formErrors?.end}
                                            onChange={(date: Date | null) => {
                                                handleChangeSeasonEdition({
                                                    end: date,
                                                });
                                            }}
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                            shouldDisableDate={(day: Date) => {
                                                const date =
                                                    convertDateToDayjs(day);
                                                if (
                                                    date &&
                                                    (date.isBefore(
                                                        convertDateToDayjs(
                                                            activeFrom
                                                        ),
                                                        'day'
                                                    ) ||
                                                        date.isAfter(
                                                            convertDateToDayjs(
                                                                activeTo
                                                            ),
                                                            'day'
                                                        ))
                                                ) {
                                                    return true;
                                                }
                                                if (
                                                    seasonEdition.start &&
                                                    date &&
                                                    date.isBefore(
                                                        convertDateToDayjs(
                                                            seasonEdition.start
                                                        ),
                                                        'day'
                                                    )
                                                ) {
                                                    return false;
                                                }
                                                return seasons.some(
                                                    range =>
                                                        date &&
                                                        date.isSameOrAfter(
                                                            convertDateToDayjs(
                                                                range.start
                                                            ),
                                                            'day'
                                                        ) &&
                                                        date.isSameOrBefore(
                                                            convertDateToDayjs(
                                                                range.end
                                                            ),
                                                            'day'
                                                        )
                                                );
                                            }}
                                        />
                                    </GridChild>
                                </Grid>
                            </div>
                        </div>
                        <div
                            className={`flex-${
                                isNewSeason ? 'end' : 'between'
                            }`}
                        >
                            {!isNewSeason && (
                                <Button
                                    className='delete-button'
                                    onClick={() => {
                                        setDeletePopUpVisible(true);
                                    }}
                                    // text='delete'
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('delete')}
                                </Button>
                            )}

                            <div className='flex-end gap-20'>
                                <Button
                                    variant='outlined'
                                    onClick={onClose}
                                    disabled={isLoadingPanel}
                                >
                                    {translate('cancel')}
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={async () => {
                                        setValidatedForm(true);
                                        const validated = validateInputsForm();
                                        if (validated) {
                                            await handleSaveSeason();
                                        }
                                    }}
                                    // text='save'
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('save')}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </LateralPanel>
        </>
    );
};

export default SeasonPanel;
